import { useEffect, useMemo } from 'react'
import useDefaultAirport from './useDefaultAirport'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import { fetchFlightFareFamilies } from 'actions/FlightActions'
import getObjectKey from 'lib/object/getObjectKey'
import useFlightsSearchInsuranceQuote from './Insurance/useFlightsSearchInsuranceQuote'
import { cloneWithInsurance, getCheapestEconomyFareFamilyWithBaggage } from 'lib/flights/flightUtils'
import { getFlightSearchInsuranceQuoteKey, isStandaloneMaldivesCharterFlight } from 'lib/insurance/insuranceFlights'
import useOptimizelyExperiment from './Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import isBookingProtectionEnabled from 'checkout/selectors/request/isBookingProtectionEnabled'
import { FlightViewTypes } from 'constants/flight'
import { shouldFetchInsuranceQuotes } from 'lib/insurance/insuranceUtils'
import { getFeederOriginAirportCode } from 'checkout/selectors/view/flights'

interface Props {
  searchId: string
  sessionId: string
  destinationAirportCode: string
  destinationCountry?: string
  departingDate?: string
  arrivalDate?: string
  occupancies?: App.Occupants
  journeyId?: string
  originAirportCode?: string
  departingJourneyId?: string
  provider?: App.FlightProviders
  isStandalone?: boolean
  isReturnType?: boolean
  isCombinedUpsell?: boolean
  departingFareFamily?: App.FlightFareFamily
  flightItem?: App.Checkout.FlightItem
  cachedFareFamilies?: App.FlightsState['fareFamiliesByFlight'][0]
}

const EmptyFareFamilyState: App.FlightsState['fareFamiliesByFlight'][0] = {
  families: [],
  familiesPerLeg: [],
  error: undefined,
  fetching: true,
}

function useFlightFareFamilies({
  searchId,
  sessionId,
  occupancies,
  destinationAirportCode,
  destinationCountry,
  journeyId,
  originAirportCode,
  departingJourneyId,
  provider,
  isStandalone,
  departingDate,
  arrivalDate,
  isReturnType,
  isCombinedUpsell,
  departingFareFamily,
  flightItem,
  cachedFareFamilies,
}: Props) {
  const departingFareFamilyId = departingFareFamily?.id
  const isCroFlexibleFlightFaresEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croFlexibleFlightFares)
  const bookingProtectionEnabled = useAppSelector(state => isBookingProtectionEnabled(state))
  const headlessMode = useAppSelector(state => state.system.headlessMode)

  const defaultAirport = useDefaultAirport()
  const origin = originAirportCode ?? defaultAirport?.code
  const feederOriginAirportCode = useAppSelector(getFeederOriginAirportCode)

  const dispatch = useAppDispatch()
  const shouldHideEmbeddedInsurance = isStandaloneMaldivesCharterFlight({
    isStandalone,
    provider,
    originAirportCode: origin,
    destinationAirportCode,
  })

  const filterKey = getObjectKey({
    origin,
    destination: destinationAirportCode,
    fareId: journeyId,
    searchId,
    departingFareId: departingJourneyId,
    departingUpsellOptionId: flightItem?.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ? undefined : departingFareFamilyId,
    feederOriginAirportCode,
  })

  useEffect(() => {
    if (!cachedFareFamilies && destinationAirportCode && journeyId && occupancies && origin) {
      dispatch(fetchFlightFareFamilies({
        origin,
        destination: destinationAirportCode,
        journeyId,
        occupancies,
        searchId,
        sessionId,
        departingJourneyId,
        departingFareFamilyId: flightItem?.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ? undefined : departingFareFamilyId,
        isStandalone,
        feederOriginAirportCode,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, cachedFareFamilies])

  const fareFamilyFromSelector = useAppSelector(state => state.flights.fareFamiliesByFlight[filterKey])

  const fareFamilyState = cachedFareFamilies !== undefined ? cachedFareFamilies : (fareFamilyFromSelector ?? EmptyFareFamilyState)

  const canTriggerBundledFlexibleFlightFaresExperiment = !headlessMode && !isStandalone && bookingProtectionEnabled && !!fareFamilyState && !fareFamilyState.fetching
  const isCroBundledFlexibleFlightFaresEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croBundledFlexibleFlightFaresV2, canTriggerBundledFlexibleFlightFaresExperiment)

  const { fetching: fetchingInsuranceQuote, quotes: insuranceQuotes } = useFlightsSearchInsuranceQuote(
    fareFamilyState,
    occupancies,
    destinationCountry ? [destinationCountry] : [],
    departingDate,
    arrivalDate,
    {
      disabled: !shouldFetchInsuranceQuotes({
        isStandalone,
        isCroFlexibleFlightFaresEnabled,
        isCroBundledFlexibleFlightFaresEnabled,
        bookingProtectionEnabled,
        isFlightDateChange: !!cachedFareFamilies,
      }) || shouldHideEmbeddedInsurance,
    },
    isReturnType,
    isCombinedUpsell,
    departingFareFamily,
  )

  const fareFamiliesStateWithInsurance = useMemo(() => {
    if (!isCroFlexibleFlightFaresEnabled || shouldHideEmbeddedInsurance) return fareFamilyState?.families || []

    if (!fareFamilyState?.families?.length || fetchingInsuranceQuote) return []

    const { cheapestEconomyWithBaggage, pairedReturningFareFamily, journeyTotalCost } = getCheapestEconomyFareFamilyWithBaggage(fareFamilyState.families, isReturnType)
    if (!cheapestEconomyWithBaggage) return fareFamilyState.families

    return fareFamilyState.families.reduce((acc: Array<App.FlightFareFamily>, family) => {
      acc.push(family)
      if (family === cheapestEconomyWithBaggage) {
        if (isReturnType && !pairedReturningFareFamily) return acc

        const quoteKey = getFlightSearchInsuranceQuoteKey(
          departingDate,
          arrivalDate,
          journeyTotalCost + (!isReturnType && departingFareFamily?.withEmbeddedInsurance ? departingFareFamily.cost.all : 0),
          destinationCountry,
          occupancies,
        )

        const quote = insuranceQuotes[quoteKey]

        if (quote) {
          const insuranceVersion = cloneWithInsurance(family, quote, occupancies, isReturnType)
          acc.push(insuranceVersion)

          if (pairedReturningFareFamily) {
            const pairedInsuranceVersion = cloneWithInsurance(pairedReturningFareFamily, quote, occupancies, isReturnType)
            pairedInsuranceVersion.pairedUpsellIds = undefined
            insuranceVersion.pairedUpsellIds = [pairedInsuranceVersion.id]
            acc.push(pairedInsuranceVersion)
          }
        }
      }
      return acc
    }, [])
  }, [isCroFlexibleFlightFaresEnabled, fareFamilyState?.families, fetchingInsuranceQuote, departingDate, arrivalDate, destinationCountry, occupancies, insuranceQuotes, isReturnType, departingFareFamily?.withEmbeddedInsurance, departingFareFamily?.cost.all, shouldHideEmbeddedInsurance])

  const fareFamiliesStatePerLegWithInsurance = useMemo(() => {
    const familiesPerLeg = fareFamilyState?.familiesPerLeg

    if (!isCroFlexibleFlightFaresEnabled || shouldHideEmbeddedInsurance) return familiesPerLeg || []
    if (!familiesPerLeg?.length || fetchingInsuranceQuote) return []

    const result: Array<Array<App.FlightFareFamily>> = familiesPerLeg.length === 1 ? [[]] : [[], []]
    const isMixedUpsell = fareFamilyState?.isMixedUpsell ?? false
    const addedReturningFamiliesById = isMixedUpsell ? new Map<string, App.FlightFareFamily>() : null

    const departingEligible = getCheapestEconomyFareFamilyWithBaggage(familiesPerLeg[0], false)?.cheapestEconomyWithBaggage
    const linkedReturningEligible = getCheapestEconomyFareFamilyWithBaggage(
      familiesPerLeg[1]?.filter(returnFamily => departingEligible && returnFamily.pairedUpsellIds?.includes(departingEligible.id)),
      false,
    )?.cheapestEconomyWithBaggage

    for (const departingFamily of familiesPerLeg[0]) {
      const cloneDepartingFamily = { ...departingFamily }
      result[0].push(cloneDepartingFamily)

      let departingInsuranceVersion: App.FlightFareFamily | undefined

      if (cloneDepartingFamily.id === departingEligible?.id) {
        const quoteKey = getFlightSearchInsuranceQuoteKey(
          departingDate,
          arrivalDate,
          cloneDepartingFamily.cost.all,
          destinationCountry,
          occupancies,
        )

        const quote = insuranceQuotes[quoteKey]

        if (quote) {
          departingInsuranceVersion = cloneWithInsurance(cloneDepartingFamily, quote, occupancies, false)
          result[0].push(departingInsuranceVersion)
        }
      }

      const pairedReturnFamilies = familiesPerLeg[1]?.filter(returnFamily => returnFamily.pairedUpsellIds?.includes(departingFamily.id)) ?? []
      const looseReturningEligible = getCheapestEconomyFareFamilyWithBaggage(pairedReturnFamilies, false)?.cheapestEconomyWithBaggage

      for (const returningFamily of pairedReturnFamilies) {
        const cloneReturningFamily = { ...returningFamily, pairedUpsellIds: [...returningFamily.pairedUpsellIds ?? []] }
        if (departingInsuranceVersion) {
          cloneReturningFamily.pairedUpsellIds = [...(returningFamily.pairedUpsellIds ?? []), departingInsuranceVersion.id]
        }

        // For LCC (isMixedUpsell), each returning family can be paired with multiple departing families.
        // Only push the base returning fare once; insurance variants are created per-departing-family below
        // so they are not deduplicated — they carry distinct pairedUpsellIds.
        const isFirstSeen = !addedReturningFamiliesById?.has(returningFamily.id)
        if (isFirstSeen) {
          addedReturningFamiliesById?.set(returningFamily.id, cloneReturningFamily)
          result[1].push(cloneReturningFamily)
        } else if (addedReturningFamiliesById) {
          const existing = addedReturningFamiliesById.get(returningFamily.id)
          if (existing && departingInsuranceVersion && !existing.pairedUpsellIds?.includes(departingInsuranceVersion.id)) {
            existing.pairedUpsellIds = [...(existing.pairedUpsellIds ?? []), departingInsuranceVersion.id]
          }
        }

        if (cloneReturningFamily.id === looseReturningEligible?.id) {
          const quoteKey = getFlightSearchInsuranceQuoteKey(
            departingDate,
            arrivalDate,
            cloneReturningFamily.cost.all,
            destinationCountry,
            occupancies,
          )

          const quote = insuranceQuotes[quoteKey]

          if (quote) {
            const returningInsuranceVersion = cloneWithInsurance(cloneReturningFamily, quote, occupancies, false)
            returningInsuranceVersion.pairedUpsellIds = [cloneDepartingFamily.id]
            result[1].push(returningInsuranceVersion)
          }
        }

        if (departingInsuranceVersion && cloneReturningFamily.id === linkedReturningEligible?.id) {
          const quoteKey = getFlightSearchInsuranceQuoteKey(
            departingDate,
            arrivalDate,
            (departingEligible?.cost.all ?? 0) + (linkedReturningEligible?.cost.all ?? 0),
            destinationCountry,
            occupancies,
          )

          const quote = insuranceQuotes[quoteKey]

          if (quote) {
            const returningInsuranceVersion = cloneWithInsurance(cloneReturningFamily, quote, occupancies, false, '2')
            returningInsuranceVersion.pairedUpsellIds = [departingInsuranceVersion.id]

            const departingInsuranceCost = {
              adult: (departingInsuranceVersion.costWithInsurance?.adult ?? 0) - (departingInsuranceVersion.cost.adult ?? 0),
              children: (departingInsuranceVersion.costWithInsurance?.children ?? 0) - (departingInsuranceVersion.cost.children ?? 0),
              infant: (departingInsuranceVersion.costWithInsurance?.infant ?? 0) - (departingInsuranceVersion.cost.infant ?? 0),
              all: (departingInsuranceVersion.costWithInsurance?.all ?? 0) - (departingInsuranceVersion.cost.all ?? 0),
            }

            /* The departingInsuranceVersion contains quote for departing flight only, but the returningInsuranceVersion contains quote for both departing and returning flights,
            so we need this subtraction for consistently showing the correct price in the Combined Fare Family Select Modal */
            returningInsuranceVersion.costWithInsurance = {
              adult: (returningInsuranceVersion.costWithInsurance?.adult ?? 0) - departingInsuranceCost.adult,
              children: (returningInsuranceVersion.costWithInsurance?.children ?? 0) - departingInsuranceCost.children,
              infant: (returningInsuranceVersion.costWithInsurance?.infant ?? 0) - departingInsuranceCost.infant,
              all: (returningInsuranceVersion.costWithInsurance?.all ?? 0) - departingInsuranceCost.all,
            }

            result[1].push(returningInsuranceVersion)
          }
        }
      }
    }

    return result
  }, [
    fareFamilyState?.familiesPerLeg,
    fareFamilyState?.isMixedUpsell,
    isCroFlexibleFlightFaresEnabled,
    fetchingInsuranceQuote,
    departingDate,
    arrivalDate,
    destinationCountry,
    occupancies,
    insuranceQuotes,
    shouldHideEmbeddedInsurance,
  ])

  return {
    families: fareFamiliesStateWithInsurance,
    familiesPerLeg: fareFamiliesStatePerLegWithInsurance,
    fetching: !!fareFamilyState?.fetching || fetchingInsuranceQuote,
    error: fareFamilyState?.error,
    fareFamiliesState: fareFamilyState?.families,
    fareFamiliesKey: filterKey,
    insuranceQuotes,
  }
}

export default useFlightFareFamilies
